/**
 * ドメイン設定。**Keycloak の realm 設定そのもの**を画面の言葉で出し入れする。
 *
 * 画面に出していない realm のキーは触らない(API が GET → 要るキーだけ差し替え →
 * PUT する)。`passwordPolicy` も、ここに無い語(hashIterations など)はそのまま残す。
 *
 * ここで作っていないもの(HENNGE One にはあるが、この基盤では別の仕組みで賄う):
 *   - 認証方法の4択・ドメイン名を無視・セキュアブラウザ・入場証クッキー
 *     → `server/app/idp/README.md` に理由を書いてある。
 *
 * 端末証明書(R7e)は作った。ここにあるのは**インストールメールの文面だけ**で、
 * 発行・失効は「ユーザー > 端末証明書」、判定はアクセスポリシーの条件式が持つ。
 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
  Upload,
  message,
} from "antd";
import { useEffect, useRef, useState } from "react";

import type { DomainForm } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { DeviceCertMailCard } from "./DeviceCertMailCard";
import { deviceCertMailFilled, fillDeviceCertMail } from "./deviceCertOptions";
import { errorText, useIdpDomain, useIdpPerms, useInvalidateIdp } from "./hooks";
import {
  LOCALES,
  LOCKOUT_COUNT_MAX,
  LOCKOUT_MINUTES_MAX,
  LOGO_ACCEPT,
  LOGO_FORMATS,
  LOGO_MAX_FILE_BYTES,
  LOGO_MAX_SIDE,
  OTP_DIGITS,
  OTP_PERIODS,
  PW_COMPLEXITY_MAX,
  PW_EXPIRY_MAX_DAYS,
  PW_HISTORY_MAX,
  PW_LENGTH_MAX,
  PW_LENGTH_MIN,
  SSO_IDLE_MAX_MINUTES,
  SSO_IDLE_MIN_MINUTES,
  SSO_MAX_MAX_HOURS,
  SSO_MAX_MIN_HOURS,
  TIMEZONES,
} from "./userOptions";

const { Text, Paragraph } = Typography;

/** 画像を data URL にする(そのまま設定に入れて、ログイン画面が絶対 URL で読む)。 */
function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("画像を読めなかった"));
    reader.readAsDataURL(file);
  });
}

export function DomainPage() {
  const api = useApi();
  const invalidate = useInvalidateIdp();
  const { canAdmin } = useIdpPerms();
  const query = useIdpDomain();
  const [form] = Form.useForm<DomainForm>();
  const [logo, setLogo] = useState("");

  /**
   * サーバーの値を form に流してよいか。**入力中の欄を消さないため**の門。
   *
   * 画面に焦点が戻ると React Query は取り直す(`refetchOnWindowFocus`)。その返りを
   * 無条件に `setFieldsValue` すると、**打っている途中の欄が消える**
   * (2026-09-29 の E2E: 説明文を打った直後の取り直しで空になり、空のまま保存された)。
   *
   * 門は **`isFieldsTouched()` では作れない**(自己レビュー 2 巡目の中 3 件):
   *   - `setFieldsValue` 自体が欄を touched にするので、一度流したあとは
   *     **サーバーの新しい値が永久に入らない**(離れて戻ると古いまま)
   *   - 初回の取得が遅れている間に打った分は `isFieldsTouched()` に関係なく消える
   *   - ロゴは Form の外(`useState`)なので touched に出ない
   *
   * 代わりに**利用者が触った欄の名前**を覚える(`onValuesChange` とロゴの 2 か所。
   * `setFieldsValue` では増えない)。流すのは**触っていない欄だけ**。
   * 門を「form 全体」にすると、初回の取得が届く前に 1 文字打っただけで
   * 残りの欄が空のままになり、**そのまま保存すると設定が消える**。
   */
  const touched = useRef(new Set<string>());
  const logoTouched = useRef(false);
  /**
   * **サーバーの値を form に入れ終わるまでは保存させない**(独立レビュー 高 2)。
   *
   * form は空の状態から始まる。取得が届く前に 1 欄だけ打って保存すると、
   * 残りの欄は空・既定値のまま PUT され、**realm の設定が消える**
   * (ページタイトル・ロックアウト・パスワード・セッション・OTP・ロゴ)。
   * 触った欄を覚える仕掛け(上)は「取得が届いた後の取り直し」を守るもので、
   * 「まだ何も取れていない」は守れない。だから門をもう 1 枚置く。
   *
   * 門を開けるのは **`query.data` が来たとき**ではなく、下の `useEffect` が
   * `setFieldsValue` と `setLogo` を**やり終えたとき**(独立レビュー 2 巡目の高)。
   * `data` を見た描画で開けると、キャッシュから即座に返ったときに「欄は空・
   * ロゴも空なのに保存できる」1 描画ぶんの隙間が残る。だから ref ではなく
   * state にして、値を流した後に立てる。
   *
   * 一度立てたら下ろさない。取り直しが失敗しても手元にはサーバーの値があるので、
   * 保存できなくする理由が無い。
   */
  const [loaded, setLoaded] = useState(false);
  /** 保存してよい = 権限があり、かつサーバーの値を手元に持っている。 */
  const canEdit = canAdmin && loaded;
  const edits = useRef(0);
  const submitted = useRef(0);
  /** 利用者が触った欄を覚える。**サーバーの値を流す側からは呼ばない**。 */
  const touch = (changed: Record<string, unknown>) => {
    edits.current += 1;
    for (const key of Object.keys(changed)) touched.current.add(key);
  };
  const touchLogo = () => { edits.current += 1; logoTouched.current = true; };

  useEffect(() => {
    if (!query.data) return;
    // 端末証明書のメールは OS × 言語で 10 個ある。realm に一部しか入っていないことが
    // あるので、空文字で埋めてから流す(欠けたまま PUT すると言語ごと消える)。
    const next: Record<string, unknown> = {
      ...query.data.form,
      device_cert_mail: fillDeviceCertMail(query.data.form.device_cert_mail),
    };
    // 触った欄だけ外す(残りは取り直しのたびに新しい値で更新してよい)
    for (const key of touched.current) delete next[key];
    form.setFieldsValue(next as DomainForm);
    if (!logoTouched.current) setLogo(query.data.form.logo ?? "");
    // **流し終えてから**門を開ける(上の註)。2 回目以降は同じ値なので
    // React が再描画を省く
    setLoaded(true);
  }, [query.data, form]);

  const save = useMutation({
    mutationFn: (v: DomainForm) => {
      // 画面の門(`canEdit`)をすり抜けても**送らない**。ここが最後の砦で、
      // 「サーバーの値を手元に持っていない状態の PUT」は形として作らない
      if (!loaded) {
        return Promise.reject(new Error("設定をまだ読み込んでいない(開き直す)"));
      }
      const body: Record<string, unknown> = { ...v };
      // ロゴは Form の外(`useState`)。上の門を通っている = サーバーの値を
      // 流し終えた後なので、ここでの `logo` は「サーバーの値か、利用者が
      // 差し替えた値」のどちらか。空文字のまま「消す」指示になることはない
      body.logo = logo;
      // サーバーがまだ `device_cert_mail` を返していない(= R7e 前)ときに、
      // 空の 10 通で既定を上書きしない。誰も文面を入れていなければ鍵ごと送らない。
      if (!query.data?.form.device_cert_mail && !deviceCertMailFilled(v.device_cert_mail)) {
        delete body.device_cert_mail;
      }
      return api.saveIdpDomain(body as DomainForm);
    },
    onSuccess: (res) => {
      // 保存が通った後は、サーバーが受けた値で流し直してよい。
      // ただし**送ってから後に触っていたら忘れない**(保存中に打った分は送れていない)
      if (edits.current === submitted.current) {
        touched.current.clear();
        logoTouched.current = false;
      }
      invalidate();
      const changed = Object.keys(res.diff ?? {}).length;
      void message.success(changed > 0 ? `保存した(${changed} 件変更)` : "変更はなかった");
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const num = (min: number, max: number) => ({ min, max, style: { width: 160 } });

  return (
    <div>
      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          ドメイン設定
        </Typography.Title>
        {canAdmin && (
          <Space>
            {!loaded && !query.isError && <Spin size="small" />}
            <Button
              type="primary"
              loading={save.isPending}
              disabled={!canEdit}
              onClick={() => void form.submit()}
            >
              保存
            </Button>
          </Space>
        )}
      </Flex>

      <Paragraph type="secondary">
        ログイン画面・パスワード・セッションの決まりを変える。ここでの保存は
        Keycloak の realm 設定を直に書き換える(画面に出していない設定は触らない)。
      </Paragraph>

      {!loaded && !query.isError && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="現在の設定を読み込んでいる"
          description="読み終わるまでは編集も保存もできない(空のまま保存して設定を消さないため)。"
        />
      )}
      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            loaded
              ? "ドメイン設定を取り直せなかった"
              : "ドメイン設定を取得できなかったので保存できない"
          }
          description={
            loaded
              ? errorText(query.error)
              : `${errorText(query.error)} / この画面は Keycloak の現在値を読んでから`
                + "差分だけを書き戻す。読めないまま保存すると設定が消えるので、"
                + "保存は止めてある。"
          }
        />
      )}
      {query.data?.logo_warning && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={query.data.logo_warning}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        disabled={!canEdit}
        onValuesChange={touch}
        onFinish={(v) => {
          submitted.current = edits.current;   // この版を送る(後で触ったら流し直さない)
          save.mutate(v);
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Card size="small" title="ログイン">
            <Form.Item name="page_title" label="ログイン画面のタイトル(OTP の発行者名にもなる)">
              <Input maxLength={200} style={{ maxWidth: 420 }} />
            </Form.Item>
            <Form.Item
              label={`ロゴ(140×90 程度。${LOGO_FORMATS}、${Math.floor(LOGO_MAX_FILE_BYTES / 1024)}KB まで。${LOGO_MAX_SIDE}px 以内の静止画)`}
            >
              <Space direction="vertical">
                {logo && <img src={logo} alt="ロゴ" style={{ maxWidth: 200, maxHeight: 90 }} />}
                <Space>
                  <Upload
                    accept={LOGO_ACCEPT}
                    maxCount={1}
                    showUploadList={false}
                    beforeUpload={(f) => {
                      if (f.size > LOGO_MAX_FILE_BYTES) {
                        void message.error(
                          `ロゴは ${Math.floor(LOGO_MAX_FILE_BYTES / 1024)}KB まで`,
                        );
                        return Upload.LIST_IGNORE;
                      }
                      // **読み終わる前に**印を付ける(読んでいる間の取り直しで
                      // サーバーのロゴに戻されないように)
                      touchLogo();
                      void readDataUrl(f).then(setLogo);
                      return false;
                    }}
                  >
                    <Button disabled={!canEdit}>画像を選ぶ</Button>
                  </Upload>
                  {logo && (
                    <Button danger disabled={!canEdit} onClick={() => { touchLogo(); setLogo(""); }}>
                      ロゴを消す
                    </Button>
                  )}
                </Space>
                {query.data?.logo_url && (
                  <Text type="secondary">
                    ログイン画面はこの URL で読む: <Text code>{query.data.logo_url}</Text>
                  </Text>
                )}
              </Space>
            </Form.Item>
            <Form.Item name="login_with_email" label="メールアドレスでもログインできる" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="remember_me" label="「ログインしたままにする」を出す" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Flex gap={16} wrap>
              <Form.Item
                name="lockout_count"
                label="連続失敗でロック(回。0 でロックしない)"
              >
                <InputNumber {...num(0, LOCKOUT_COUNT_MAX)} />
              </Form.Item>
              <Form.Item name="lockout_minutes" label="ロックする時間(分)">
                <InputNumber {...num(1, LOCKOUT_MINUTES_MAX)} />
              </Form.Item>
              <Form.Item
                name="permanent_lockout"
                label="解除は管理者だけ(自動で解けない)"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Flex>
          </Card>

          <Card size="small" title="パスワード">
            <Flex gap={16} wrap>
              <Form.Item name="password_min_length" label="最低文字数">
                <InputNumber {...num(PW_LENGTH_MIN, PW_LENGTH_MAX)} />
              </Form.Item>
              <Form.Item name="password_history" label="使い回しを禁じる回数(0 で無し)">
                <InputNumber {...num(0, PW_HISTORY_MAX)} />
              </Form.Item>
              <Form.Item name="password_expiry_days" label="有効期限(日。0 で無期限)">
                <InputNumber {...num(0, PW_EXPIRY_MAX_DAYS)} />
              </Form.Item>
            </Flex>
            <Flex gap={16} wrap>
              <Form.Item name="password_upper" label="大文字の数(0 で不要)">
                <InputNumber {...num(0, PW_COMPLEXITY_MAX)} />
              </Form.Item>
              <Form.Item name="password_lower" label="小文字の数(0 で不要)">
                <InputNumber {...num(0, PW_COMPLEXITY_MAX)} />
              </Form.Item>
              <Form.Item name="password_digit" label="数字の数(0 で不要)">
                <InputNumber {...num(0, PW_COMPLEXITY_MAX)} />
              </Form.Item>
              <Form.Item name="password_special" label="記号の数(0 で不要)">
                <InputNumber {...num(0, PW_COMPLEXITY_MAX)} />
              </Form.Item>
              <Form.Item
                name="password_not_username"
                label="ユーザー名と同じにしない"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                name="password_self_reset"
                label="本人がパスワードを再設定できる"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Flex>
            <Form.Item
              name="password_note"
              label="ポータルに出す説明(パスワードの決まりを本人向けに書く)"
            >
              <Input.TextArea rows={3} maxLength={1000} showCount />
            </Form.Item>
            {/*
              Keycloak の原文。**畳まずに常に出す**(2026-09-28: 折り畳みの中にあったので
              「欄が無い」と読まれた)。読めなかったときも欄は残し、空欄と区別できるよう
              「読めなかった」と書く。
            */}
            <Form.Item label="Keycloak の passwordPolicy(そのままの文字列)">
              <Space direction="vertical" style={{ width: "100%" }}>
                {query.isLoading ? (
                  <Text type="secondary">読み込み中…</Text>
                ) : query.isError ? (
                  <Text type="warning">
                    Keycloak から現在値を読めなかった(上のエラーを見る)。
                  </Text>
                ) : (
                  <Text code>{query.data?.policy || "(Keycloak 側で未設定)"}</Text>
                )}
                <Text type="secondary">
                  画面に無い語(hashIterations など)は、位置も値もそのまま残す。
                </Text>
                {(query.data?.policy_warnings?.length ?? 0) > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message="この画面からは保存できません"
                    description={
                      <>
                        {query.data?.policy_warnings.join(" / ")}
                        。Keycloak 側で直してください。
                      </>
                    }
                  />
                )}
              </Space>
            </Form.Item>
          </Card>

          <Card size="small" title="セッション">
            <Flex gap={16} wrap>
              <Form.Item name="sso_idle_minutes" label="無操作で切れるまで(分)">
                <InputNumber {...num(SSO_IDLE_MIN_MINUTES, SSO_IDLE_MAX_MINUTES)} />
              </Form.Item>
              <Form.Item name="sso_max_hours" label="最長の在席時間(時間)">
                <InputNumber {...num(SSO_MAX_MIN_HOURS, SSO_MAX_MAX_HOURS)} />
              </Form.Item>
            </Flex>
          </Card>

          <Card size="small" title="その他">
            <Flex gap={16} wrap>
              <Form.Item name="locale" label="ログイン画面の言語">
                <Select style={{ width: 160 }} options={LOCALES} />
              </Form.Item>
              <Form.Item name="timezone" label="表示するタイムゾーン(スイート側)">
                <Select
                  style={{ width: 200 }}
                  options={TIMEZONES.map((t) => ({ value: t, label: t }))}
                />
              </Form.Item>
              <Form.Item name="otp_digits" label="OTP の桁数">
                <Select
                  style={{ width: 120 }}
                  options={OTP_DIGITS.map((d) => ({ value: d, label: String(d) }))}
                />
              </Form.Item>
              <Form.Item name="otp_period" label="OTP の有効秒数">
                <Select
                  style={{ width: 120 }}
                  options={OTP_PERIODS.map((d) => ({ value: d, label: String(d) }))}
                />
              </Form.Item>
            </Flex>
            <Text type="secondary">
              OTP の発行者名(認証アプリに出る名前)はログイン画面のタイトルと同じになる。
            </Text>
          </Card>

          <DeviceCertMailCard canEdit={canEdit} />
        </Space>
      </Form>
    </div>
  );
}
